import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface IngredientsMatchProps {
  concern: string;
  skinConcerns: string[];
  satisfaction: number;
  onNext: () => void;
}

interface Ingredient {
  name: string;
  subtitle: string;
  description: string;
  tags: string[];
}

const allIngredients: Record<string, Ingredient> = {
  peptiden: {
    name: "5-Voudig Peptide Complex",
    subtitle: "Het hart van de formule",
    description: "Argireline® ontspant expressielijntjes, Matrixyl® 3000 stimuleert collageenproductie, en drie tetrapeptiden versterken huidstructuur en stevigheid.",
    tags: ["Rimpels", "Stevigheid"],
  },
  ergothioneine: {
    name: "Ergothioneïne",
    subtitle: "De 'longevity antioxidant'",
    description: "Beschermt huidcellen tegen oxidatieve stress en UV-schade, remt collageen-afbraak en ondersteunt natuurlijk herstel.",
    tags: ["Bescherming", "Anti-aging"],
  },
  hyaluronzuur: {
    name: "Hyaluronzuur",
    subtitle: "Intense hydratatie van binnenuit",
    description: "Houdt tot 1000× het eigen gewicht aan vocht vast. Via micro-infusie bereikt het de diepere huidlagen voor vollere, vochtiger huid.",
    tags: ["Hydratatie", "Volume"],
  },
  neroli: {
    name: "Neroli Olie",
    subtitle: "Kalmering & huidherstel",
    description: "Kalmeert de huid na micro-infusie, ondersteunt celvernieuwing en heeft antibacteriële eigenschappen.",
    tags: ["Kalmering", "Herstel"],
  },
};

// Map skinConcerns to relevant ingredient keys
const skinConcernToIngredients: Record<string, string[]> = {
  wrinkles: ["peptiden", "ergothioneine"],
  firmness: ["peptiden", "hyaluronzuur"],
  dullness: ["hyaluronzuur", "ergothioneine"],
  dryness: ["hyaluronzuur", "neroli"],
  pigment: ["ergothioneine", "neroli"],
  sagging: ["peptiden", "hyaluronzuur"],
};

const getIngredientsForProfile = (concern: string, skinConcerns: string[]) => {
  // Use skinConcerns if available, otherwise fall back to concern
  const ingredientKeys = new Set<string>();

  if (skinConcerns.length > 0) {
    skinConcerns.forEach(sc => {
      const keys = skinConcernToIngredients[sc] || [];
      keys.forEach(k => ingredientKeys.add(k));
    });
  } else {
    // Fallback to primary concern
    const fallbackMap: Record<string, string[]> = {
      wrinkles: ["peptiden", "ergothioneine", "hyaluronzuur"],
      firmness: ["peptiden", "hyaluronzuur", "ergothioneine"],
      dullness: ["hyaluronzuur", "ergothioneine", "neroli"],
      all: ["peptiden", "ergothioneine", "hyaluronzuur", "neroli"],
    };
    (fallbackMap[concern] || fallbackMap["all"]).forEach(k => ingredientKeys.add(k));
  }

  return Array.from(ingredientKeys).map(k => allIngredients[k]);
};

const getSatisfactionIntro = (satisfaction: number) => {
  if (satisfaction <= 3) {
    return `Je gaf aan heel ontevreden te zijn met je huid. Op basis van jouw aangegeven klachten zijn deze ingrediënten precies geselecteerd om daar verandering in te brengen.`;
  }
  if (satisfaction <= 5) {
    return `Je gaf je huid een ${satisfaction} van de 10. Op basis van jouw aangegeven klachten hebben we de volgende ingrediënten geselecteerd om dat te verbeteren.`;
  }
  return `Op basis van jouw aangegeven klachten hebben we de volgende ingrediënten voor je geselecteerd.`;
};

const IngredientsMatch = ({ concern, skinConcerns, satisfaction, onNext }: IngredientsMatchProps) => {
  const [phase, setPhase] = useState(0);
  const ingredients = getIngredientsForProfile(concern, skinConcerns);
  const intro = getSatisfactionIntro(satisfaction);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 400),
      setTimeout(() => setPhase(2), 1500 + ingredients.length * 400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [ingredients.length]);

  return (
    <div className="text-center">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="font-sans text-sm uppercase tracking-wider text-accent font-bold mb-3"
      >
        Ons advies voor jouw huid
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl md:text-4xl font-serif font-medium text-foreground mb-2 leading-tight"
      >
        {ingredients.length} ingrediënten geselecteerd voor jouw huid
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-foreground/80 font-sans text-lg md:text-xl mb-6 md:mb-10 max-w-md mx-auto leading-relaxed"
      >
        {intro}
      </motion.p>

      {/* Ingredients cards */}
      <AnimatePresence>
        {phase >= 1 && (
          <div className="space-y-3 md:space-y-4 mb-5 md:mb-8">
            {ingredients.map((ing, i) => (
              <motion.div
                key={ing.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * i, duration: 0.35 }}
                className="insight-card text-left p-5"
              >
                <div className="mb-2">
                  <h3 className="font-serif text-xl font-semibold text-foreground">
                    {ing.name}
                  </h3>
                  <p className="font-sans text-[17px] text-accent font-medium">
                    {ing.subtitle}
                  </p>
                </div>
                <p className="font-sans text-lg text-foreground/80 leading-relaxed mb-3">
                  {ing.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {ing.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-sans text-xs font-medium px-3 py-1 rounded-full bg-secondary text-foreground/80"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <div className="mt-5 md:mt-8">
        <button onClick={onNext} className="btn-cta">
          Ga verder met mijn analyse
        </button>
      </div>
    </div>
  );
};

export default IngredientsMatch;
